import { useState, useEffect } from 'react';
import {
  Plus,
  Copy,
  Trash2,
  Check,
  X,
  Edit2,
  Key,
  RefreshCw,
  Clock,
  Server,
  AlertCircle,
  Activity,
  ShieldCheck,
  Shield,
} from 'lucide-react';
import {
  imageKeys,
  imageBackends,
  ImageApiKey,
  ImageBackend,
  CreateImageKeyRequest,
  UpdateImageKeyRequest,
} from '../../api/client';
import { formatDateTime, formatNumber, cn } from '../../lib/utils';

export default function ImageKeys() {
  const [keys, setKeys] = useState<ImageApiKey[]>([]);
  const [backends, setBackends] = useState<ImageBackend[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingKey, setEditingKey] = useState<ImageApiKey | null>(null);
  const [newKeyValue, setNewKeyValue] = useState<string | null>(null);
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      const [keysRes, backendsRes] = await Promise.all([
        imageKeys.list(),
        imageBackends.list(),
      ]);
      setKeys(keysRes.keys);
      setBackends(backendsRes.backends);
      setError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch data';
      console.error('Failed to fetch data:', error);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateKey = async (data: CreateImageKeyRequest) => {
    try {
      setError(null);
      const response = await imageKeys.create(data);
      setNewKeyValue(response.key.apiKey);
      setShowCreateModal(false);
      await fetchData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create key';
      console.error('Failed to create key:', error);
      setError(message);
    }
  };

  const handleUpdateKey = async (id: string, data: UpdateImageKeyRequest) => {
    try {
      setError(null);
      await imageKeys.update(id, data);
      setEditingKey(null);
      await fetchData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update key';
      console.error('Failed to update key:', error);
      setError(message);
    }
  };

  const handleToggleActive = async (key: ImageApiKey) => {
    try {
      await imageKeys.update(key.id, { isActive: !key.isActive });
      await fetchData();
    } catch (error) {
      console.error('Failed to toggle key:', error);
    }
  };

  const handleDeleteKey = async (key: ImageApiKey) => {
    if (!confirm(`Are you sure you want to delete the API key "${key.name}"?`)) {
      return;
    }

    try {
      await imageKeys.delete(key.id);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete key:', error);
    }
  };

  const copyToClipboard = async (text: string, keyId?: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedKeyId(keyId || 'new');
    setTimeout(() => setCopiedKeyId(null), 2000);
  };

  const getBackendName = (backendId: string | null) => {
    if (!backendId) return '-';
    const backend = backends.find(b => b.id === backendId);
    return backend?.name || backendId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading API keys...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient-violet">Image API Keys</h1>
          <p className="text-muted-foreground mt-1">Manage image generation access tokens</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-glow-violet px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm"
        >
          <span className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Create Key
          </span>
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-5 animate-scale-in">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-rose-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-rose-400 mb-1">Error</p>
              <p className="text-sm text-rose-300/80">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="p-2 rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* New Key Created Alert */}
      {newKeyValue && (
        <div className="alert-success rounded-xl p-5 animate-scale-in">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Key className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium mb-2">API key created successfully!</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 code-text px-4 py-3 rounded-lg font-mono text-sm break-all">
                  {newKeyValue}
                </code>
                <button
                  onClick={() => copyToClipboard(newKeyValue)}
                  className={cn(
                    'p-3 rounded-lg transition-all',
                    copiedKeyId === 'new'
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-secondary text-muted-foreground hover:text-foreground'
                  )}
                >
                  {copiedKeyId === 'new' ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => setNewKeyValue(null)}
                  className="p-3 rounded-lg bg-secondary text-muted-foreground hover:text-foreground transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Save this key now. You won't be able to see it again!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Keys Table */}
      <div className="card-glow rounded-2xl overflow-hidden animate-fade-in-up animate-delay-100">
        <table className="table-neural min-w-full">
          <thead>
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Backend
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Security
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Usage Today
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {keys.map((key, index) => (
              <tr
                key={key.id}
                className="animate-fade-in-up"
                style={{ animationDelay: `${(index + 2) * 50}ms` }}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-violet-500/10 flex items-center justify-center flex-shrink-0">
                      <Key className="w-4 h-4 text-violet-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{key.name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="text-xs text-muted-foreground font-mono truncate max-w-[200px]" title={key.fullKey || `sk-img-${key.keyPrefix}...`}>
                          {key.fullKey || `sk-img-${key.keyPrefix}...`}
                        </code>
                        {key.fullKey && (
                          <button
                            onClick={() => copyToClipboard(key.fullKey!, key.id)}
                            className={cn(
                              'p-1 rounded transition-all flex-shrink-0',
                              copiedKeyId === key.id
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-secondary text-muted-foreground hover:text-violet-400'
                            )}
                            title="Copy API key"
                          >
                            {copiedKeyId === key.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Server className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">{getBackendName(key.defaultBackend)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-muted-foreground font-mono capitalize">
                      Log: {key.logLevel}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleToggleActive(key)}
                    className={cn(
                      'px-3 py-1.5 text-xs font-medium rounded-lg transition-all',
                      key.isActive ? 'badge-active' : 'badge-inactive'
                    )}
                  >
                    {key.isActive ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {key.requireAttestation ? (
                      <>
                        <ShieldCheck className="w-4 h-4 text-emerald-400" />
                        <span className="text-xs font-medium text-emerald-400">App Attest</span>
                      </>
                    ) : (
                      <>
                        <Shield className="w-4 h-4 text-muted-foreground/50" />
                        <span className="text-xs text-muted-foreground/50">None</span>
                      </>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-violet-400" />
                    <span className="text-sm font-mono">
                      {formatNumber(key.usage?.requestsToday ?? 0)} requests
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    {formatDateTime(key.createdAt)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => setEditingKey(key)}
                      className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-violet-400 transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteKey(key)}
                      className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-rose-400 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {keys.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center">
                      <Key className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">No image API keys yet</p>
                      <p className="text-sm text-muted-foreground/70">Create one to start generating images</p>
                    </div>
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="btn-secondary px-4 py-2 rounded-lg text-sm"
                    >
                      Create your first key
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create Key Modal */}
      {showCreateModal && (
        <KeyModal
          backends={backends}
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => handleCreateKey(data as CreateImageKeyRequest)}
        />
      )}

      {/* Edit Key Modal */}
      {editingKey && (
        <KeyModal
          backends={backends}
          existingKey={editingKey}
          onClose={() => setEditingKey(null)}
          onSubmit={(data) => handleUpdateKey(editingKey.id, data)}
        />
      )}
    </div>
  );
}

function KeyModal({
  backends,
  existingKey,
  onClose,
  onSubmit,
}: {
  backends: ImageBackend[];
  existingKey?: ImageApiKey;
  onClose: () => void;
  onSubmit: (data: CreateImageKeyRequest | UpdateImageKeyRequest) => void;
}) {
  const isEditing = !!existingKey;

  const [name, setName] = useState(existingKey?.name || '');
  const [defaultBackend, setDefaultBackend] = useState(existingKey?.defaultBackend || '');
  const [logLevel, setLogLevel] = useState<'none' | 'basic' | 'full'>(existingKey?.logLevel || 'basic');
  const [requireAttestation, setRequireAttestation] = useState(existingKey?.requireAttestation ?? true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditing) {
      const data: UpdateImageKeyRequest = {};
      if (name !== existingKey.name) data.name = name;
      data.defaultBackend = defaultBackend || null;
      data.logLevel = logLevel;
      data.requireAttestation = requireAttestation;
      onSubmit(data);
    } else {
      const data: CreateImageKeyRequest = { name };
      if (defaultBackend) data.defaultBackend = defaultBackend;
      data.logLevel = logLevel;
      data.requireAttestation = requireAttestation;
      onSubmit(data);
    }
  };

  return (
    <div className="fixed inset-0 modal-overlay flex items-center justify-center z-50 p-4">
      <div
        className="modal-content rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
            {isEditing ? (
              <Edit2 className="w-5 h-5 text-violet-400" />
            ) : (
              <Key className="w-5 h-5 text-violet-400" />
            )}
          </div>
          <div>
            <h2 className="text-lg font-semibold">
              {isEditing ? 'Edit Image API Key' : 'Create Image API Key'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isEditing ? 'Update key configuration' : 'Configure a new image generation token'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Name
            </label>
            <input
              type="text"
              required={!isEditing}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm"
              placeholder="PersonaLLM iOS App"
            />
          </div>

          {/* Backend Selection */}
          <div className="card-glow rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Server className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-medium">Backend Configuration</span>
            </div>

            <div>
              <label className="block text-sm text-muted-foreground mb-2">Default Backend</label>
              <select
                value={defaultBackend}
                onChange={(e) => setDefaultBackend(e.target.value)}
                className="input-neural w-full px-4 py-3 rounded-xl text-sm appearance-none cursor-pointer"
              >
                <option value="">Select a backend...</option>
                {backends.filter(b => b.isActive).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} ({b.type})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-muted-foreground mb-2">Log Level</label>
              <select
                value={logLevel}
                onChange={(e) => setLogLevel(e.target.value as 'none' | 'basic' | 'full')}
                className="input-neural w-full px-4 py-3 rounded-xl text-sm appearance-none cursor-pointer"
              >
                <option value="none">None - No logging</option>
                <option value="basic">Basic - Metadata only</option>
                <option value="full">Full - Include workflow params</option>
              </select>
            </div>
          </div>

          {/* Security */}
          <div className="card-glow rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-medium">Security</span>
            </div>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <div className="text-sm font-medium">Require App Attest</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Only accept image requests from devices verified by Apple App Attest.
                </p>
              </div>
              <div className="relative ml-4 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={requireAttestation}
                  onChange={(e) => setRequireAttestation(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 rounded-full bg-secondary peer-checked:bg-emerald-500/30 transition-colors" />
                <div className="absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-muted-foreground peer-checked:bg-emerald-400 peer-checked:translate-x-5 transition-all" />
              </div>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary px-5 py-2.5 rounded-xl text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-glow-violet px-5 py-2.5 rounded-xl text-sm"
            >
              <span>{isEditing ? 'Save Changes' : 'Create Key'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
